import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

const SESSION_KEY = 'selora_popups_shown';

function getShown() {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}'); } catch { return {}; }
}
function markShown(id) {
  const shown = getShown();
  shown[id] = true;
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(shown));
}
function wasShown(id) { return !!getShown()[id]; }

function PopupShell({ title, text, buttons, onClose, testId }) {
  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm w-full animate-in" data-testid={testId}>
      <div className="bg-white rounded-xl shadow-2xl border border-[#E8E4DC] p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-[#999] hover:text-[#1A2F4A] transition-colors" data-testid={`${testId}-close`}>
          <X className="w-4 h-4" />
        </button>
        <h3 className="text-lg font-bold text-[#1A2F4A] mb-1.5 pr-6" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h3>
        <p className="text-sm text-[#6B4E37] mb-4 leading-relaxed">{text}</p>
        <div className="flex flex-wrap gap-2">
          {buttons.map((btn, i) => (
            <Link
              key={i}
              to={btn.to}
              onClick={onClose}
              className={i === 0
                ? 'bg-[#1A2F4A] hover:bg-[#243a58] text-white text-xs font-semibold px-4 py-2 rounded-full transition-all'
                : 'border border-[#E8E4DC] text-[#1A2F4A] text-xs font-semibold px-4 py-2 rounded-full hover:border-[#D4AF37] transition-all'}
              data-testid={`${testId}-btn-${i}`}>
              {btn.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// 1. Exit Intent
export function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (wasShown('exit')) return;
    const handler = (e) => {
      if (e.clientY <= 5) { setShow(true); markShown('exit'); }
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, []);
  if (!show) return null;
  return <PopupShell testId="popup-exit" title="Avant de partir…" text="Commencez à parler français dès aujourd'hui."
    buttons={[{ label: 'Explorer le Speaking Lab', to: '/speaking-lab' }, { label: 'Rejoindre le Club Francophone', to: '/club-francophone' }]}
    onClose={() => setShow(false)} />;
}

// 2. Time-based (Levels pages)
export function TimePopup() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (wasShown('time')) return;
    const t = setTimeout(() => { setShow(true); markShown('time'); }, 60000);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return <PopupShell testId="popup-time" title="Prêt à passer à l'oral ?" text="Mettez en pratique ce que vous apprenez dans le Speaking Lab."
    buttons={[{ label: 'Aller au Speaking Lab', to: '/speaking-lab' }]}
    onClose={() => setShow(false)} />;
}

// 3. Scroll-based (Speaking Lab)
export function ScrollPopup() {
  const [show, setShow] = useState(false);
  const handleScroll = useCallback(() => {
    if (wasShown('scroll')) return;
    const scrollPct = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
    if (scrollPct >= 0.7) { setShow(true); markShown('scroll'); }
  }, []);
  useEffect(() => {
    if (wasShown('scroll')) return;
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);
  if (!show) return null;
  return <PopupShell testId="popup-scroll" title="Envie de parler en direct ?" text="Rejoignez des conversations réelles avec des enseignants et apprenants."
    buttons={[{ label: 'Découvrir le Club Francophone', to: '/club-francophone' }]}
    onClose={() => setShow(false)} />;
}

// 4. Completion (end of activities)
export function CompletionPopup() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (wasShown('complete')) return;
    const handler = () => {
      const el = document.querySelector('[data-testid="section-approfondir"]');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight) { setShow(true); markShown('complete'); }
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  if (!show) return null;
  return <PopupShell testId="popup-complete" title="Excellent travail" text="Continuez votre progression à l'oral avec un enseignant ou en groupe."
    buttons={[{ label: 'Sessions avec enseignant', to: '/club-francophone' }, { label: 'Club Francophone', to: '/club-francophone' }]}
    onClose={() => setShow(false)} />;
}
